import React, { useEffect } from 'react';
import { DetailsPage } from 'app/layout/pages/DetailsPage';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import useDocumentTitle from 'utils/customHooks/useDocumentTitle';
import { pageNames } from 'app/constants/PageNames';
import MarginToolDetails from './MarginToolDetails';
import { Typography, useTheme } from '@mui/material';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';

import { useDispatch, useSelector } from 'react-redux';
import { useDecision } from '@optimizely/react-sdk';
import { ff_placement_ui_arbitration_agreement_grids } from 'app/constants/FeatureFlags/Global/keys';
import {
  ff_placement_ui_peoplesoft_sync,
  ff_placement_ui_requirement_check,
  ff_placement_check_for_classic_packets_in_ok_to_send_status,
  ff_peformance_enhanced_placement_profile_page,
} from 'app/constants/FeatureFlags/Placement/Keys';
import {
  placementDetailReducer,
  placementDetailsAction,
  placementDetailSliceKey,
} from 'store/redux-store/placement-details/slice';
import {
  placementEventsSliceKey,
  placementEventsReducer,
} from '@AMIEWEB/Placement/PlacementDetails/PlacementTabPanel/CredentialingTab/Store/PlacementEventDetails.redux';
import { placementStatusSliceKey, placementStatusReducer } from 'store/redux-store/placement-status/slice';
import { placementDetailsSaga } from 'store/redux-store/placement-details/saga';
import { placementSummaryConsolidatedStoreSaga } from 'store/redux-store/placement-summary/consolidatedStoreSaga';
import {
  placementSummaryConsolidatedStoreSliceKey,
  placementSummaryConsolidatedStoreReducer,
  placementSummaryConsolidatedStoreActions,
} from 'store/redux-store/placement-summary/consolidatedStoreSlice';
import { placementSummarySaga } from 'store/redux-store/placement-summary/saga';
import { placementSummarySliceKey, placementSummaryReducer } from 'store/redux-store/placement-summary/slice';
import {
  placementMarginScenarioActions,
  placementMarginScenarioReducer,
  placementMarginScenarioSliceKey,
} from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.redux';
import { placementMarginScenarioSaga } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.saga';
import { MarginEntityType } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.model';
import { selectPlacementMarginPageLoadStatus } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.selector';
import {
  placementMarginAssignmentSliceKey,
  placementMarginAssignmentReducer,
} from 'store/redux-store/margin-tool/slices/placement/placement-assignment/placement-assignment.redux';
import { placementMarginAssignmentSaga } from 'store/redux-store/margin-tool/slices/placement/placement-assignment/placement-assignment.saga';
import {
  placementMarginToolEmailSliceKey,
  placementMarginToolEmailReducer,
} from 'store/redux-store/margin-tool/slices/placement/placement-margin-tool-email/placement-margin-tool-email.redux';
import { placementMarginToolEmailSaga } from 'store/redux-store/margin-tool/slices/placement/placement-margin-tool-email/placement-margin-tool-email.saga';
import {
  placementMarginPayPackageStatusSliceKey,
  placementMarginPayPackageStatusSliceReducer,
} from 'store/redux-store/margin-tool/slices/placement/placement-pay-package-status/placement-pay-package-status.redux';
import { placementMarginPayPackageStatusSaga } from 'store/redux-store/margin-tool/slices/placement/placement-pay-package-status/placement-pay-package-status.saga';

export const MarginToolWrapper = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  useInjectReducer({ key: placementMarginScenarioSliceKey, reducer: placementMarginScenarioReducer });
  useInjectSaga({ key: placementMarginScenarioSliceKey, saga: placementMarginScenarioSaga });
  useInjectReducer({ key: placementMarginAssignmentSliceKey, reducer: placementMarginAssignmentReducer });
  useInjectSaga({ key: placementMarginAssignmentSliceKey, saga: placementMarginAssignmentSaga });
  useInjectReducer({
    key: placementMarginPayPackageStatusSliceKey,
    reducer: placementMarginPayPackageStatusSliceReducer,
  });
  useInjectSaga({ key: placementMarginPayPackageStatusSliceKey, saga: placementMarginPayPackageStatusSaga });
  useInjectSaga({ key: placementMarginToolEmailSliceKey, saga: placementMarginToolEmailSaga });
  useInjectReducer({ key: placementMarginToolEmailSliceKey, reducer: placementMarginToolEmailReducer });

  useInjectReducer({ key: placementStatusSliceKey, reducer: placementStatusReducer });
  useInjectReducer({ key: placementDetailSliceKey, reducer: placementDetailReducer });
  useInjectReducer({ key: placementEventsSliceKey, reducer: placementEventsReducer });
  useInjectSaga({ key: placementDetailSliceKey, saga: placementDetailsSaga });

  useInjectReducer({
    key: placementSummaryConsolidatedStoreSliceKey,
    reducer: placementSummaryConsolidatedStoreReducer,
  });
  useInjectSaga({ key: placementSummaryConsolidatedStoreSliceKey, saga: placementSummaryConsolidatedStoreSaga });

  useInjectReducer({ key: placementSummarySliceKey, reducer: placementSummaryReducer });
  useInjectSaga({ key: placementSummarySliceKey, saga: placementSummarySaga });

  const [enablePeoplesoft] = useDecision(ff_placement_ui_peoplesoft_sync);
  const [requirementCheckFlag] = useDecision(ff_placement_ui_requirement_check);
  const [checkClassicPacketInOkToSendFlag] = useDecision(ff_placement_check_for_classic_packets_in_ok_to_send_status);
  const [arbitrationAgreementFlag] = useDecision(ff_placement_ui_arbitration_agreement_grids);
  const [performanceOptimizedPageLoad] = useDecision(ff_peformance_enhanced_placement_profile_page);

  const params = useParams<{ entity: string; entityId: string; scenarioId?: string }>();
  const location = useLocation();
  const isPageLoaded = useSelector(selectPlacementMarginPageLoadStatus);

  useDocumentTitle({
    title: params?.entityId ? `${pageNames.marginTool} ${params.entityId}` : `${pageNames.marginTool}`,
  });

  const setMarginToolBreadCrumbs = (
    title: string,
    color: string = theme.palette.primary.main,
    isLink: boolean = true,
    fontWeight: string = 'medium',
  ) => {
    return (
      <Typography sx={{ color: color, pointerEvents: isLink ? 'auto' : 'none', fontWeight: fontWeight }}>
        {title}
      </Typography>
    );
  };

  const placementId = params.entityId && params.entity === 'placement' ? Number(params.entityId) : null;

  const breadcrumbItems = {
    items: [
      ...(placementId
        ? [
            {
              title: setMarginToolBreadCrumbs(`${t('templateEditor.placement.allPlacements')}`),
              path: '/search/placement',
              clickable: true,
            },
            {
              title: setMarginToolBreadCrumbs(`PID ${placementId}`),
              path: `/placement/${placementId}`,
              clickable: true,
            },
          ]
        : [
            {
              title: setMarginToolBreadCrumbs('WorkDesk'),
              path: '/',
              clickable: true,
            },
          ]),
      {
        title: setMarginToolBreadCrumbs(`${t('marginTool.marginTool')}`, theme.palette.system.black, false, 'bold'),
        clickable: false,
      },
    ],
  };

  useEffect(() => {
    if (params.entityId && params.entity === MarginEntityType.Placement) {
      const placementIdLc = Number(params.entityId);
      if (placementIdLc > 0) {
        // Preferred: scenarioId from optional route param (/margin/placement/:placementId/:scenarioId?)
        // Backward-compatible: also allow ?scenarioId=... (existing deep links)
        const scenarioIdFromRoute = params.scenarioId ? Number(params.scenarioId) : undefined;
        const scenarioIdParam = new URLSearchParams(location.search).get('scenarioId');
        const scenarioIdFromQuery = scenarioIdParam ? Number(scenarioIdParam) : undefined;
        const scenarioId = Number.isFinite(scenarioIdFromRoute) ? scenarioIdFromRoute : scenarioIdFromQuery;
        dispatch(
          placementMarginScenarioActions.getPlacementMarginToolDetails({
            placementId: placementIdLc,
            scenarioId: Number.isFinite(scenarioId) ? scenarioId : undefined,
          }),
        );
        dispatch(
          placementDetailsAction.onInitAction({
            placementId: placementIdLc,
            peoplesoftEnabled: enablePeoplesoft?.enabled,
            requirementCheckFlagEnabled: requirementCheckFlag.enabled,
            checkClassicPacketInOkToSendEnabled: checkClassicPacketInOkToSendFlag?.enabled,
            arbitrationAgreementFlagEnabled: arbitrationAgreementFlag?.enabled,
            isPerformanceOptimized: performanceOptimizedPageLoad?.enabled,
          }),
        );
      } else if (!isPageLoaded) {
        dispatch(placementMarginScenarioActions.setPlacementMarginPageLoadStatus(true));
      }
    } else if (!isPageLoaded) {
      dispatch(placementMarginScenarioActions.setPlacementMarginPageLoadStatus(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.entityId, params.scenarioId, location.search]);

  useEffect(() => {
    return () => {
      dispatch(placementMarginScenarioActions.reset());
      dispatch(placementDetailsAction.reset());
      dispatch(placementSummaryConsolidatedStoreActions.reset());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DetailsPage
      children={<MarginToolDetails />}
      breadcrum={breadcrumbItems}
      isCreateForm={false}
      paddingBottom="4rem"
      titleActions={<></>}
      head={null}
    />
  );
};
